import { useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { List, X, SignOut, Globe } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { RiskNotice } from "@/components/layout/PublicLayout";
import { Loader } from "@/components/Blocks";
import { PreviewBanner } from "@/components/PreviewBanner";

const SideNav = ({ items, onNav, prefix = "side-nav" }) => (
  <nav className="space-y-0.5">
    {items.map(([to, label, Icon, end]) => (
      <NavLink key={to} to={to} end={end} onClick={onNav} data-testid={`${prefix}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-[#E0A21B] font-semibold text-[#0B1B33]" : "text-white/75 hover:bg-white/5 hover:text-white"}`}>
        <Icon size={18} weight="duotone" /> {label}
      </NavLink>
    ))}
  </nav>
);

export default function DashShell({ items, label, allow }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  if (allow && !allow(user)) return <Navigate to="/app" replace />;
  const out = async () => { await logout(); nav("/"); };
  const side = (prefix) => (
    <div className="flex h-full flex-col">
      <Link to="/" className="flex items-center gap-2 px-3 pb-6">
        <img src="/brand/11_App_Icon_512x512.png" alt="Mission Control Trading" className="h-9 w-9 rounded" />
        <div className="font-display text-lg font-extrabold uppercase leading-none text-white">Mission Control<div className="text-xs tracking-[0.3em] text-[#E0A21B]">{label}</div></div>
      </Link>
      <div className="flex-1 overflow-y-auto"><SideNav items={items} prefix={prefix} onNav={() => setOpen(false)} /></div>
      <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
        <div className="px-3 text-xs text-white/50">{user.name} · <span className="uppercase">{user.role}</span></div>
        <Link to="/" className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-white/70 hover:text-white" data-testid="dash-public-site-link"><Globe size={18} /> Public site</Link>
        <button onClick={out} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-white/70 hover:text-[#E0A21B]" data-testid="logout-btn"><SignOut size={18} /> Sign out</button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#F4F6FA] lg:flex">
      <aside className="no-print sticky top-0 hidden h-screen w-64 shrink-0 bg-[#0B1B33] p-4 lg:block">{side("side-nav")}</aside>
      <div className="no-print sticky top-0 z-30 flex items-center justify-between bg-[#0B1B33] px-4 py-3 lg:hidden">
        <span className="font-display text-lg font-extrabold uppercase text-white">MCT <span className="text-[#E0A21B]">{label}</span></span>
        <button onClick={() => setOpen(!open)} className="text-white" aria-label="Menu" data-testid="dash-mobile-toggle">{open ? <X size={26} /> : <List size={26} />}</button>
      </div>
      {open && <div className="no-print fixed inset-0 z-40 bg-[#0B1B33] p-4 pt-16 lg:hidden">{side("mobile-nav")}</div>}
      <main className="min-w-0 flex-1">
        <div className="no-print"><PreviewBanner /></div>
        <div className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-8"><Outlet /></div>
        <div className="no-print mx-auto max-w-6xl px-4 pb-6 sm:px-6 lg:px-8"><RiskNotice /></div>
      </main>
    </div>
  );
}

export const PageTitle = ({ title, sub, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">{title}</h1>
      {sub && <p className="mt-1 text-sm text-[#475569]">{sub}</p>}
    </div>
    {children}
  </div>
);

export const Stat = ({ label, value, accent = "#E0A21B", testid }) => (
  <div className="panel p-4" data-testid={testid}>
    <div className="text-xs font-bold uppercase tracking-widest text-[#475569]">{label}</div>
    <div className="mt-2 font-mono-num text-3xl font-semibold" style={{ color: accent === "#E0A21B" ? "#0B1B33" : accent }}>{value}</div>
    <div className="mt-3 h-1 w-10 rounded-full" style={{ background: accent }} />
  </div>
);

export const Empty = ({ children }) => <div className="panel p-8 text-sm text-[#475569]" data-testid="empty-state">{children}</div>;
